import * as XLSX from 'xlsx-js-style';
import { getColumnsKeys } from './keys';
import { getSheetCopy } from './copy';
import { getColumnsCount, getRowsCount } from './count';

export interface ColumnsWidths {
  [key: string]: number;
}

const defaultHeaderStyles: XLSX.CellStyle = {
  alignment: {
    horizontal: 'center',
    vertical: 'center',
    wrapText: true,
  },
  fill: {
    patternType: 'solid',
    fgColor: { rgb: 'F2F2F2' },
    bgColor: { rgb: 'F2F2F2' },
  },
  font: {
    bold: true,
  },
};

/**
 * Adds styling to the header of the sheet.
 *
 * @param sheet XLSX Sheet object.
 * @param [styles] Styles to add.
 * @returns sheet with header styling.
 */
export const withHeaderStyling = (sheet: XLSX.WorkSheet, styles: XLSX.CellStyle = defaultHeaderStyles): XLSX.WorkSheet => {
  const copy = JSON.parse(JSON.stringify(sheet)) as XLSX.WorkSheet;

  const headerKeys = Object.keys(copy).filter((key) => key.match(/[A-Z]+[1]$/));
  for (const key of headerKeys) {
    copy[key].s = styles;
  }
  return copy;
};

/**
 * Set width of all columns to fixed value in pixels.
 *
 * @param sheet XLSX Sheet object.
 * @param [width] Width in pixels (points).
 * @returns sheet with set columns width.
 */
export const withAllColumnsWidth = (sheet: XLSX.WorkSheet, width = 90): XLSX.WorkSheet => {
  const copy = JSON.parse(JSON.stringify(sheet)) as XLSX.WorkSheet;
  const sizes: XLSX.ColInfo[] = [];
  const columnsCount = getColumnsCount(copy);
  for (let i = 0; i < columnsCount; i++) {
    sizes.push({ wpx: width });
  }
  copy['!cols'] = sizes;
  return copy;
};

/**
 * Set height of all rows to fixed value in pixels.
 *
 * @param sheet XLSX Sheet object.
 * @param [height] height in pixels (points).
 * @returns sheet with set rows height.
 */
export const withAllRowsHeight = (sheet: XLSX.WorkSheet, height = 30): XLSX.WorkSheet => {
  const copy = JSON.parse(JSON.stringify(sheet)) as XLSX.WorkSheet;
  const sizes: XLSX.RowInfo[] = [];
  const rowsCount = getRowsCount(copy);
  for (let i = 0; i < rowsCount; i++) {
    sizes.push({ hpx: height });
  }
  copy['!rows'] = sizes;
  return copy;
};

/**
 * Set height of header row to fixed value in pixels.
 *
 * @param sheet XLSX Sheet object.
 * @param [height] height in pixels (points).
 * @returns sheet with set rows height.
 */
export const withHeaderHeight = (sheet: XLSX.WorkSheet, height = 30): XLSX.WorkSheet => {
  const copy = JSON.parse(JSON.stringify(sheet)) as XLSX.WorkSheet;
  const sizes: XLSX.RowInfo[] = [];
  sizes.push({ hpx: height });
  copy['!rows'] = sizes;
  return copy;
};

/**
 * Set width of column with given value to fixed value in pixels.
 * If column with given value does not exist, nothing happens.
 *
 * @param sheet XLSX Sheet object.
 * @param value Column value.
 * @param [width] Width in pixels (points).
 * @returns sheet with set column width.
 */
export const withColumnWithValueWidth = (sheet: XLSX.WorkSheet, value: string, width = 90): XLSX.WorkSheet => {
  const copy = getSheetCopy(sheet);
  const columnKeys = getColumnsKeys(copy);

  for (let i = 0; i < columnKeys.length; i++) {
    const key = columnKeys[i];
    const cell = copy[key];
    if (cell.v === value) {
      copy['!cols'][i] = { ...copy['!cols'][i], wpx: width };
      return copy;
    }
  }
  return copy;
};

/**
 * Set width of columns with given values to fixed value in pixels .
 * If columns with given values does not exist, nothing happens.
 *
 * @param sheet XLSX Sheet object.
 * @param values Columns width values - { column_value: column_width }.
 * @returns sheet with set column width.
 */
export const withColumnsWithValuesWidth = (sheet: XLSX.WorkSheet, values: ColumnsWidths[]): XLSX.WorkSheet => {
  const copy = getSheetCopy(sheet);
  const columnKeys = getColumnsKeys(copy);

  for (let i = 0; i < columnKeys.length; i++) {
    const key = columnKeys[i];
    const cell = copy[key];
    for (const value of values) {
      const columnKey = Object.keys(value)[0];
      if (cell.v === columnKey) {
        const columnWidth = value[cell.v];
        copy['!cols'][i] = { ...copy['!cols'][i], wpx: columnWidth };
        break;
      }
    }
  }
  return copy;
};

/**
 * Sets the height of a specified row in an Excel worksheet.
 *
 * @param ws The worksheet to modify.
 * @param rowIndex The one-based index of the row to set the height for. **Warning**: watch out of one-based and zero-based indexes.
 * @param height The desired height in pixels.
 */
export const setRowHeight = (ws: XLSX.WorkSheet, rowIndex: number, height: number): void => {
  if (!ws['!rows']) {
    ws['!rows'] = [];
  }
  ws['!rows'][rowIndex] = { hpx: height };
};

/**
 * Sets the width of a specified column in an Excel worksheet.
 *
 * @param sheet The worksheet to modify.
 * @param columnIndex The index of the column to set the width for. **Warning**: watch out of one-based and zero-based indexes.
 * @param width The desired width in character units.
 */
export const setColumnWidth = (sheet: XLSX.WorkSheet, columnIndex: number, width: number): void => {
  if (!sheet['!cols']) {
    sheet['!cols'] = [];
  }

  // Ensure the width array is large enough at least up until the desired value
  while (sheet['!cols'].length <= columnIndex) {
    sheet['!cols'].push({});
  }
  sheet['!cols'][columnIndex] = { wch: width };
};

export const defineColumnsLen = (data: string[]): number => {
  return data.sort().reverse()[0].length;
};

export const getColumnsWidths = (): ColumnsWidths[] => ([
  { Company: 180 }, { Investment: 100 }, { 'Estimated Fair value': 100 },
  { '% of rights, fully diluted': 120 }, { Country: 100 }, { Industry: 100 },
]);
